import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {Alert, App, AutoComplete, Button, Card, Empty, Input, Space, Spin, Tooltip, Typography} from "antd";
import {SaveOutlined, TrophyOutlined} from "@ant-design/icons";
import AxiosServices from "../Services/axiosServices";
import {useAuth} from "../config/AuthContext";
import {useGlobal} from "../config/GlobalContext";

const ACCENT = '#203923';
const {Title, Paragraph, Text} = Typography;

type AllowedTeam = {idt: number; nom: string};
type CmpEdtBetRow = {
    idt: number;
    Cod: string;
    Lib: string;
    allowedTeams: AllowedTeam[];
    userAnswer: string | null;
};

const CompetitionBets: FC = () => {
    const {isLoggedIn} = useAuth();
    const {currentCmpEdt} = useGlobal();
    const {message} = App.useApp();

    const [bets, setBets] = useState<CmpEdtBetRow[]>([]);
    const [drafts, setDrafts] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(false);
    const [savingIdt, setSavingIdt] = useState<number | null>(null);

    const refresh = useCallback(() => {
        if (!currentCmpEdt) return;
        setLoading(true);
        AxiosServices.listCmpEdtBets(currentCmpEdt)
            .then((rows) => {
                setBets(rows ?? []);
                const seed: Record<number, string> = {};
                (rows ?? []).forEach(r => { seed[r.idt] = r.userAnswer ?? ''; });
                setDrafts(seed);
            })
            .catch((err: any) => {
                console.error('listCmpEdtBets failed:', err);
                message.error("Impossible de charger les paris de compétition.");
            })
            .finally(() => setLoading(false));
    }, [currentCmpEdt, message]);

    useEffect(() => {
        if (!isLoggedIn) return;
        refresh();
    }, [isLoggedIn, refresh]);

    const ordered = useMemo(() => {
        return [...bets].sort((a, b) => a.Cod.localeCompare(b.Cod));
    }, [bets]);

    if (!isLoggedIn) {
        return (
            <div style={{padding: 24}}>
                <Empty description="Connecte-toi pour saisir tes paris avant la compétition."/>
            </div>
        );
    }

    if (!currentCmpEdt) {
        return (
            <div style={{padding: 24}}>
                <Empty description="Sélectionne d'abord une compétition dans la sidebar."/>
            </div>
        );
    }

    const handleSave = async (bet: CmpEdtBetRow) => {
        const value = (drafts[bet.idt] ?? '').trim();
        if (!value) {
            message.warning("Saisis une réponse avant d'enregistrer.");
            return;
        }
        if (value === (bet.userAnswer ?? '').trim()) {
            message.info('Aucune modification à enregistrer.');
            return;
        }
        setSavingIdt(bet.idt);
        try {
            await AxiosServices.saveCmpEdtBetAnswer(bet.idt, value);
            message.success(`« ${bet.Lib} » enregistré.`);
            setBets(prev => prev.map(b => b.idt === bet.idt ? {...b, userAnswer: value} : b));
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.error ?? err?.response?.data ?? err?.message;
            message.error(`Échec ${status ?? ''} : ${serverMsg ?? 'erreur inconnue'}`);
        } finally {
            setSavingIdt(null);
        }
    };

    const renderInput = (bet: CmpEdtBetRow) => {
        const value = drafts[bet.idt] ?? '';
        const set = (v: string) => setDrafts(prev => ({...prev, [bet.idt]: v}));
        if (bet.allowedTeams.length > 0) {
            const options = bet.allowedTeams.map(t => ({value: t.nom, label: t.nom}));
            return (
                <AutoComplete
                    style={{width: '100%'}}
                    options={options}
                    value={value}
                    onChange={set}
                    placeholder="Choisis une équipe…"
                    filterOption={(input, option) =>
                        (option?.value ?? '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                />
            );
        }
        return (
            <Input
                value={value}
                onChange={e => set(e.target.value)}
                placeholder="Ta réponse"
                maxLength={255}
            />
        );
    };

    return (
        <div style={{padding: 24, maxWidth: 900, margin: '0 auto'}}>
            <Title level={2} style={{color: ACCENT, textAlign: 'center'}}>
                <TrophyOutlined/> Paris avant la compétition
            </Title>
            <Paragraph type="secondary" style={{textAlign: 'center', marginBottom: 24}}>
                À effectuer avant le début de la compétition. Les paris attaque/défense sont
                calculés sur les 4 demi-finalistes.
            </Paragraph>

            {loading ? (
                <div style={{textAlign: 'center', padding: 48}}><Spin/></div>
            ) : ordered.length === 0 ? (
                <Alert
                    type="info"
                    showIcon
                    message="Aucun pari de compétition n'a encore été configuré pour cette édition."
                />
            ) : (
                <Space direction="vertical" size={12} style={{width: '100%'}}>
                    {ordered.map(bet => (
                        <Card
                            key={bet.idt}
                            size="small"
                            styles={{body: {padding: 16}}}
                            style={{borderRadius: 10, borderColor: ACCENT}}
                        >
                            <Space direction="vertical" size={8} style={{width: '100%'}}>
                                <div>
                                    <Text strong style={{fontSize: 15}}>{bet.Lib}</Text>
                                    <Text type="secondary" style={{marginLeft: 8, fontSize: 11}}>
                                        ({bet.Cod})
                                    </Text>
                                </div>
                                <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                                    <div style={{flex: 1}}>{renderInput(bet)}</div>
                                    <Tooltip title="Enregistrer ta réponse">
                                        <Button
                                            type="primary"
                                            icon={<SaveOutlined/>}
                                            loading={savingIdt === bet.idt}
                                            onClick={() => handleSave(bet)}
                                        />
                                    </Tooltip>
                                </div>
                                {bet.userAnswer && (
                                    <Text type="secondary" style={{fontSize: 11}}>
                                        Dernière réponse enregistrée : <strong>{bet.userAnswer}</strong>
                                    </Text>
                                )}
                            </Space>
                        </Card>
                    ))}
                </Space>
            )}
        </div>
    );
};

export default CompetitionBets;
